import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import type { TWorkJourneyManagementTab } from '@src/models/interfaces/workJourneyManagement.interface';
import { ALLOWED_ROLES, usePersistedFilters } from '@src/packages/utils';

import {
  WORK_JOURNEY_MANAGEMENT_FILTERS_STORAGE_KEY,
  WORK_JOURNEY_MANAGEMENT_TABS,
} from '../constants';
import { useWorkJourneyManagementContext } from '../context';
import {
  filterValuesToManagementFilters,
  managementFiltersToFilterValues,
  WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS,
} from '../utils/workJourneyManagementFilterPanel';
import {
  buildCalendarSummaryFromCells,
  buildShiftMap,
  buildShiftOptions,
  DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS,
  filterAttentionItems,
  filterCalendarCells,
  filterJourneyItems,
  hasActiveFilters,
  hasClientSideFilters,
  hasClientSideFiltersForJourneys,
  WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
  type IWorkJourneyManagementFilterOption,
  type IWorkJourneyManagementFilters,
} from '../utils/workJourneyManagementFilters';
import { useWorkJourneyScheduleAbsence } from './useWorkJourneyScheduleAbsence';

export const useWorkJourneyManagement = () => {
  const intl = useIntl();
  const {
    activeTab,
    setActiveTab,
    setJourneyFilter,
    journeyConfigs,
    attentionItems,
    attentionTotal,
    attentionPage,
    attentionPageSize,
    setAttentionPage,
    journeysSummary,
    journeyItems,
    journeysTotal,
    journeysPage,
    journeysPageSize,
    setJourneysPage,
    calendarSummary,
    calendarCells,
    selectedMonth,
    selectedYear,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousCalendarMonth,
    goToNextCalendarMonth,
    jumpToMonthYear,
    loading,
    journeysLoading,
    calendarLoading,
    error,
    journeysError,
    calendarError,
    refreshAttentionItems,
    refreshJourneys,
    refreshCalendar,
    refreshActiveTab,
    orgId,
  } = useWorkJourneyManagementContext();

  const [filters, setFilters] = useState<IWorkJourneyManagementFilters>(
    DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS,
  );
  // Label cache for FilterActiveTags only; options themselves come from Elastic search.
  const [selectedUserOption, setSelectedUserOption] =
    useState<IWorkJourneyManagementFilterOption | null>(null);
  const didHydrateFiltersRef = useRef(false);
  /** Blocks list fetches until session filters (and journeyFilter) are applied. */
  const [filtersReady, setFiltersReady] = useState(false);

  const filtersActive = hasActiveFilters(filters);
  const clientSideFiltersActive = hasClientSideFilters(filters);
  const journeysClientSideFiltersActive = hasClientSideFiltersForJourneys(filters);

  const defaultFilterValues = useMemo(
    () =>
      managementFiltersToFilterValues(
        DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS,
        intl,
        [],
        [],
        [],
      ),
    [intl],
  );

  const shiftByJourneyId = useMemo(() => buildShiftMap(journeyItems), [journeyItems]);
  const shiftOptions = useMemo(() => buildShiftOptions(journeyItems), [journeyItems]);
  const userOptions = useMemo(
    () => (selectedUserOption ? [selectedUserOption] : []),
    [selectedUserOption],
  );
  const journeyOptions = useMemo(
    () =>
      journeyConfigs.map(config => ({
        value: config.id,
        label: config.name,
      })),
    [journeyConfigs],
  );

  const persistedFilterValues = useMemo(
    () =>
      managementFiltersToFilterValues(
        filters,
        intl,
        userOptions,
        shiftOptions,
        journeyOptions,
      ),
    [filters, intl, journeyOptions, shiftOptions, userOptions],
  );

  const { initialFilters } = usePersistedFilters({
    storageKey: WORK_JOURNEY_MANAGEMENT_FILTERS_STORAGE_KEY,
    defaults: defaultFilterValues,
    values: persistedFilterValues,
  });

  useEffect(() => {
    if (didHydrateFiltersRef.current) return;
    didHydrateFiltersRef.current = true;

    const restored = filterValuesToManagementFilters(initialFilters);
    setFilters(restored);
    if (restored.journeyId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL) {
      setJourneyFilter(restored.journeyId);
    }

    const userField = initialFilters[WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.USER_ID];
    if (
      restored.userId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL &&
      typeof userField?.valueTranslated === 'string' &&
      userField.valueTranslated
    ) {
      setSelectedUserOption({
        value: restored.userId,
        label: userField.valueTranslated,
      });
    }
    setFiltersReady(true);
  }, [initialFilters, setJourneyFilter]);

  // When secondary filters force a full fetch, page clicks only slice client-side
  // — pin the server page dep so pagination does not recreate a CF round-trip.
  const attentionServerPage = clientSideFiltersActive ? 1 : attentionPage;
  const journeysServerPage =
    activeTab === 'journeys' && !journeysClientSideFiltersActive ? journeysPage : 1;

  useEffect(() => {
    if (!filtersReady) return;
    if (activeTab !== 'requires_attention') return;
    const paginate = !clientSideFiltersActive;
    refreshAttentionItems({
      paginate,
      ...(paginate ? { page: attentionServerPage } : {}),
    });
  }, [
    filtersReady,
    activeTab,
    clientSideFiltersActive,
    attentionServerPage,
    refreshAttentionItems,
  ]);

  // User membership is server-side on journeys (`userId`); status/shift stay
  // client-side and still force a full month fetch when set.
  useEffect(() => {
    if (!filtersReady) return;
    const paginate = activeTab === 'journeys' && !journeysClientSideFiltersActive;
    const userId =
      filters.userId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL ? filters.userId : undefined;
    refreshJourneys({
      paginate,
      ...(paginate ? { page: journeysServerPage } : {}),
      userId,
    });
  }, [
    filtersReady,
    activeTab,
    journeysClientSideFiltersActive,
    journeysServerPage,
    filters.userId,
    refreshJourneys,
  ]);

  // Calendar: same membership scope via listCalendar.userId; holidays stay
  // server-side regardless of that filter.
  useEffect(() => {
    if (!filtersReady) return;
    if (activeTab !== 'calendar') return;
    const userId =
      filters.userId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL ? filters.userId : undefined;
    refreshCalendar({ userId });
  }, [filtersReady, activeTab, filters.userId, refreshCalendar]);

  const updateFilters = useCallback(
    (patch: Partial<IWorkJourneyManagementFilters>) => {
      setJourneysPage(1);
      setAttentionPage(1);
      if (patch.journeyId !== undefined) {
        setJourneyFilter(patch.journeyId);
      }
      if (patch.userId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL) {
        setSelectedUserOption(null);
      }
      setFilters(current => ({ ...current, ...patch }));
    },
    [setJourneyFilter, setJourneysPage, setAttentionPage],
  );

  const resolveUserOption = useCallback((option: IWorkJourneyManagementFilterOption | null) => {
    setSelectedUserOption(option);
  }, []);

  const resetFilters = useCallback(() => {
    setJourneysPage(1);
    setAttentionPage(1);
    setJourneyFilter(DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.journeyId);
    setSelectedUserOption(null);
    setFilters(DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS);
  }, [setJourneyFilter, setJourneysPage, setAttentionPage]);

  const filteredAttentionItems = useMemo(
    () => filterAttentionItems(attentionItems, filters, shiftByJourneyId),
    [attentionItems, filters, shiftByJourneyId],
  );

  const pagedAttentionItems = useMemo(() => {
    if (!clientSideFiltersActive || activeTab !== 'requires_attention') return filteredAttentionItems;
    const from = (attentionPage - 1) * attentionPageSize;
    return filteredAttentionItems.slice(from, from + attentionPageSize);
  }, [
    activeTab,
    attentionPage,
    attentionPageSize,
    clientSideFiltersActive,
    filteredAttentionItems,
  ]);

  const filteredJourneyItems = useMemo(
    () => filterJourneyItems(journeyItems, filters),
    [filters, journeyItems],
  );

  // When secondary client-side filters force a full fetch, paginate the filtered
  // list client-side so the table still pages. User membership is server-side.
  const pagedJourneyItems = useMemo(() => {
    if (!journeysClientSideFiltersActive || activeTab !== 'journeys') {
      return filteredJourneyItems;
    }
    const from = (journeysPage - 1) * journeysPageSize;
    return filteredJourneyItems.slice(from, from + journeysPageSize);
  }, [
    activeTab,
    journeysClientSideFiltersActive,
    filteredJourneyItems,
    journeysPage,
    journeysPageSize,
  ]);

  const visibleJourneysTotal = journeysClientSideFiltersActive
    ? filteredJourneyItems.length
    : journeysTotal;

  const filteredCalendarCells = useMemo(
    () => filterCalendarCells(calendarCells, filters, shiftByJourneyId),
    [calendarCells, filters, shiftByJourneyId],
  );

  const filteredCalendarSummary = useMemo(() => {
    if (!calendarSummary || !clientSideFiltersActive) return calendarSummary;
    return buildCalendarSummaryFromCells(filteredCalendarCells, calendarSummary);
  }, [calendarSummary, clientSideFiltersActive, filteredCalendarCells]);

  const visibleAttentionTotal = clientSideFiltersActive
    ? filteredAttentionItems.length
    : attentionTotal;

  const scheduleAbsence = useWorkJourneyScheduleAbsence({
    onSaved: refreshActiveTab,
  });

  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);

  // "Replace worker" stays out until its own flow exists; replacements are
  // created from the cover drawers of each conflict.
  const headerActionButtons: FilterContainerActionButton[] = [
    {
      label: intl.formatMessage({ id: 'workJourney.management.actions.scheduleAbsence' }),
      type: 'secondary',
      action: scheduleAbsence.openDrawer,
      allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
    },
    {
      label: intl.formatMessage({ id: 'workJourney.management.actions.plan' }),
      type: 'primary',
      action: () => setPlanDrawerOpen(true),
      allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
    },
  ];

  const tabItems = WORK_JOURNEY_MANAGEMENT_TABS.map(tab => ({
    key: tab,
    label: intl.formatMessage({ id: `workJourney.management.tabs.${tab}` }),
    count: tab === 'requires_attention' ? visibleAttentionTotal : undefined,
  }));

  const handleTabChange = (tab: TWorkJourneyManagementTab) => {
    if (tab === 'journeys') setJourneysPage(1);
    if (tab === 'requires_attention') setAttentionPage(1);
    setActiveTab(tab);
  };

  const handleJourneysPageChange = useCallback(
    (page: number) => {
      setJourneysPage(page);
    },
    [setJourneysPage],
  );

  const handleAttentionPageChange = useCallback(
    (page: number) => {
      setAttentionPage(page);
    },
    [setAttentionPage],
  );

  const displayError =
    activeTab === 'journeys' ? journeysError : activeTab === 'calendar' ? calendarError : error;
  const displayLoading =
    activeTab === 'journeys'
      ? journeysLoading
      : activeTab === 'calendar'
        ? calendarLoading
        : loading;

  return {
    intl,
    orgId,
    activeTab,
    handleTabChange,
    tabItems,
    journeyOptions,
    filters,
    filtersActive,
    updateFilters,
    resolveUserOption,
    resetFilters,
    userOptions,
    shiftOptions,
    attentionItems: pagedAttentionItems,
    attentionTotal: visibleAttentionTotal,
    attentionPage,
    attentionPageSize,
    onAttentionPageChange: handleAttentionPageChange,
    journeysSummary,
    journeyItems: pagedJourneyItems,
    journeysTotal: visibleJourneysTotal,
    journeysPage,
    journeysPageSize,
    onJourneysPageChange: handleJourneysPageChange,
    calendarSummary: filteredCalendarSummary,
    calendarCells: filteredCalendarCells,
    selectedMonth,
    selectedYear,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousCalendarMonth,
    goToNextCalendarMonth,
    jumpToMonthYear,
    loading: displayLoading,
    error: displayError,
    headerActionButtons,
    scheduleAbsence,
    planDrawerOpen,
    openPlanDrawer: () => setPlanDrawerOpen(true),
    closePlanDrawer: () => setPlanDrawerOpen(false),
    refreshActiveTab,
  };
};
